import {
  EntryWithImage,
  EntryWithImageAndDescription,
  HeaderNavigationSecondLevelEntry,
  HeaderNavigationSubEntry,
  HeaderNavigationThirdLevelEntry,
} from "@/components/layout/header";
import TranslatedLink from "@/libs/base/components/translated-link";
import { cn } from "@/libs/base/utils/layout";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

interface PageProps {
  subEntries: HeaderNavigationSubEntry[];
}

export default function DesktopNavigationEntryPanel({ subEntries }: PageProps) {
  const [hoveredSubEntry, setHoveredSubEntry] = useState<HeaderNavigationSubEntry | null>(null);
  const [hoveredSecondLevelSubEntry, setHoveredSecondLevelSubEntry] = useState<HeaderNavigationSecondLevelEntry | null>(
    null,
  );
  const [hoveredThirdLevelSubEntry, setHoveredThirdLevelSubEntry] = useState<HeaderNavigationThirdLevelEntry | null>(
    null,
  );

  const [imageSource, setImageSource] = useState<string | null>(null);
  const [isImageMounted, setIsImageMounted] = useState(false);
  const [description, setDescription] = useState<string | null>(null);

  const pathname = usePathname();

  // remove first element which is always the language /de/ or /en/
  const userPath = useCallback(() => pathname.split("/").slice(2), [pathname]);

  // Removes locale and domain
  const parseUrl = useCallback((url: string) => {
    if (url.startsWith("/")) {
      return new URL(url, window.location.origin).pathname.split("/");
    }
    return new URL(url).pathname.split("/").slice(1);
  }, []);

  const setBreadcrumbs = (
    entries: (HeaderNavigationSubEntry[] | HeaderNavigationSecondLevelEntry[]) | undefined,
    path: string[],
    level = 0,
  ) => {
    if (path.length === 0 || !entries || level >= path.length) {
      return;
    }

    const currentEntry = entries.find((entry) => parseUrl(entry.url)[level] === path[level]) as
      | HeaderNavigationSubEntry
      | HeaderNavigationSecondLevelEntry
      | HeaderNavigationThirdLevelEntry;

    if (currentEntry) {
      if (level === 0) {
        setHoveredSubEntry(currentEntry as HeaderNavigationSubEntry);
        setImageAndDescription(currentEntry as HeaderNavigationSubEntry);
      } else if (level === 1) {
        setHoveredSecondLevelSubEntry(currentEntry as HeaderNavigationSecondLevelEntry);
        setImageAndDescription(currentEntry as HeaderNavigationSecondLevelEntry);
      } else if (level === 2) {
        setHoveredThirdLevelSubEntry(currentEntry as HeaderNavigationThirdLevelEntry);
        setImage(currentEntry as HeaderNavigationThirdLevelEntry);

        return;
      }

      // @ts-ignore - because we know that currentEntry has subElements
      setBreadcrumbs(currentEntry.subElements, path, level + 1);
    }
  };

  useEffect(() => {
    if (userPath().length > 0) {
      setBreadcrumbs(subEntries, userPath());
    }
  }, []);

  const setHoveredSubEntryClearOtherLevels = (subEntry: EntryWithImageAndDescription) => {
    setHoveredSubEntry(subEntry);
    setHoveredSecondLevelSubEntry(null);
    setHoveredThirdLevelSubEntry(null);
  };

  const setImage = (subEntry: EntryWithImage) => {
    if (subEntry.image) {
      setImageSource(subEntry.image);
      setIsImageMounted(true);
    } else {
      setImageSource(null);
      setIsImageMounted(false);
    }
  };

  // Only set entries if they are not null
  const setImageAndDescription = (subEntry: EntryWithImageAndDescription) => {
    setImage(subEntry);

    if (subEntry.description) {
      setDescription(subEntry.description);
    }
  };

  return (
    <div className="flex text-xl text-black">
      {/* First Column - Links */}
      <div className="w-1/4 border-r border-lightgray-300 p-9">
        <ul>
          {subEntries.map((subEntry) => (
            <TranslatedLink key={subEntry.label} href={subEntry.url}>
              <li
                key={subEntry.label}
                onMouseEnter={() => {
                  setHoveredSubEntryClearOtherLevels(subEntry);
                  setImageAndDescription(subEntry);
                }}
                className={cn(
                  "cursor-pointer transition-all duration-200 ease-in-out",
                  hoveredSubEntry === subEntry && "-skew-x-12",
                  hoveredSubEntry && hoveredSubEntry !== subEntry && hoveredSecondLevelSubEntry && "opacity-25",
                )}
              >
                {subEntry.label}
              </li>
            </TranslatedLink>
          ))}
        </ul>
      </div>

      {/* Second Column - Links of subentries, empty if there are none */}
      <div className="w-1/4 border-r border-lightgray-300 p-9">
        {hoveredSubEntry?.subElements && (
          <ul>
            {hoveredSubEntry.subElements?.map((subEntry) => (
              <TranslatedLink key={subEntry.label} href={subEntry.url}>
                <li
                  key={subEntry.label}
                  onMouseEnter={() => {
                    setHoveredSecondLevelSubEntry(subEntry);
                    setImageAndDescription(subEntry);
                  }}
                  className={cn(
                    "cursor-pointer transition-all duration-200 ease-in-out",
                    hoveredSecondLevelSubEntry === subEntry && "-skew-x-12",
                    hoveredSecondLevelSubEntry &&
                      hoveredSecondLevelSubEntry !== subEntry &&
                      hoveredThirdLevelSubEntry &&
                      "opacity-25",
                  )}
                >
                  {subEntry.label}
                </li>
              </TranslatedLink>
            ))}
          </ul>
        )}
      </div>

      {/* Third Column - Either description or links of hoveredSecondLevelSubEntry */}
      <div className="w-1/4 border-r border-lightgray-300 p-9">
        {description && hoveredSecondLevelSubEntry?.subElements?.length === undefined && (
          <p className="font-light text-black">{description}</p>
        )}
        {hoveredSecondLevelSubEntry?.subElements && (
          <ul>
            {hoveredSecondLevelSubEntry.subElements.map((subEntry) => (
              <TranslatedLink key={subEntry.label} href={subEntry.url}>
                <li
                  key={subEntry.label}
                  onMouseEnter={() => {
                    setHoveredThirdLevelSubEntry(subEntry);
                    if (subEntry.image) {
                      setImageSource(subEntry.image);
                      setIsImageMounted(true);
                    } else {
                      setImageSource(null);
                      setIsImageMounted(false);
                    }
                  }}
                  onMouseLeave={() => {
                    setHoveredThirdLevelSubEntry(null);
                    setImageAndDescription(hoveredSecondLevelSubEntry);
                  }}
                  className={cn(
                    "cursor-pointer transition-all duration-300 ease-in-out",
                    hoveredThirdLevelSubEntry === subEntry && "-skew-x-12",
                  )}
                >
                  {subEntry.label}
                </li>
              </TranslatedLink>
            ))}
          </ul>
        )}
      </div>

      {/* Fourth Column - Image */}
      <div className="relative aspect-1 w-1/4">
        {imageSource && (
          <img
            key={imageSource}
            src={imageSource}
            alt="Image"
            className={cn("h-full w-full object-cover", isImageMounted ? "animate-fade-in" : "animate-fade-out")}
            onAnimationEnd={() => {
              if (!isImageMounted) {
                setImageSource(null);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
